import React, { useMemo, useState } from 'react';
import { TrackerCatalyst, TrackerWatchDoc } from '../../types';
import TrackerIcon from './TrackerIcon';
import { addDays, favPassed, favRead, fmtDay, shortWindow, today } from './helpers';

interface Props {
  rows: TrackerCatalyst[];
  watch: TrackerWatchDoc;
  onRemove: (id: string) => void;
  onOpen: (row: TrackerCatalyst) => void;
  onMarkRead: (id: string) => void;
  onMarkAllRead: () => void;
  hidden: boolean;
  onHide: () => void;
  watchOnly: boolean;
  onToggleWatchOnly: () => void;
  onOpenAlerts: () => void;
}

/** The favorites strip from tracker.js's renderWatchPanel() (`.wpanel`/`.wleft`/`.wright`/`.wscroll`/`.wchip`). */
const WatchPanel: React.FC<Props> = ({ rows, watch, onRemove, onOpen, onMarkRead, onMarkAllRead, hidden, onHide, watchOnly, onToggleWatchOnly, onOpenAlerts }) => {
  const [expanded, setExpanded] = useState(false);
  const watched = useMemo(() => {
    const byId = new Map(rows.map((r) => [r.id, r]));
    return (watch.catalysts || []).map((id) => byId.get(id)).filter(Boolean) as TrackerCatalyst[];
  }, [rows, watch.catalysts]);

  if (hidden || !watched.length) return null;

  const TODAY = today();
  const due14 = watched.filter((r) => r.status === 'upcoming' && r.date && r.date >= TODAY && r.date <= addDays(TODAY, 14)).length;
  const unread = watched.filter((r) => favPassed(r) && !favRead(r, watch.read));

  return (
    <div className="wpanel">
      <div className="wleft">
        <span className="lbl"><span className="star"><TrackerIcon name="star" /></span>Favorites <span className="wcount">{watched.length}</span></span>
        <div className={`wscroll${expanded ? ' exp' : ''}`}>
          {watched.map((r) => {
            const isUnread = favPassed(r) && !favRead(r, watch.read);
            const isDone = favPassed(r) && !isUnread;
            return (
              <span key={r.id} className={`wchip${isUnread ? ' unread' : ''}${isDone ? ' done' : ''}`}>
                {isUnread && <span className="wdot" />}
                {isDone && <span className="wdone">&#10003;</span>}
                <button type="button" className="linkish" style={{ color: 'inherit', padding: 0 }} onClick={() => { onOpen(r); if (isUnread) onMarkRead(r.id); }}>
                  {r.asset || r.company}
                </button>
                <span>{shortWindow(r) || fmtDay(r.date)}</span>
                <button type="button" onClick={() => onRemove(r.id)} aria-label="Remove from favorites"><TrackerIcon name="x" /></button>
              </span>
            );
          })}
        </div>
      </div>
      <div className="wright">
        <span className="wsum">
          {unread.length > 0 && <><b className="wnew">{unread.length}</b> new result{unread.length === 1 ? '' : 's'} · </>}
          <b>{due14}</b> due in 14 days
        </span>
        {unread.length > 0 && (
          <button type="button" className="ib sm" title="Mark all as read" onClick={onMarkAllRead}><TrackerIcon name="checkall" /></button>
        )}
        <button type="button" className="ib sm wexp" aria-pressed={expanded} title={expanded ? 'Collapse' : 'Expand'} onClick={() => setExpanded((v) => !v)}>
          <TrackerIcon name="chev" />
        </button>
        <button type="button" className="ib sm" aria-pressed={watchOnly} title="Show only favorites" aria-label="Show only favorites" onClick={onToggleWatchOnly}>
          <TrackerIcon name="list" />
        </button>
        <button
          type="button"
          className="ib sm"
          title={`Email alerts${watch.emailAlerts ? ' (on)' : ''}`}
          aria-label="Email alerts"
          style={watch.emailAlerts ? { color: 'var(--accent)', borderColor: 'var(--accent)' } : undefined}
          onClick={onOpenAlerts}
        >
          <TrackerIcon name="bell" />
        </button>
        <button type="button" className="ib sm tip-r" title="Hide this panel" aria-label="Hide favorites panel" onClick={onHide}>
          <TrackerIcon name="x" />
        </button>
      </div>
    </div>
  );
};

export default WatchPanel;
